import React, { useState, useEffect } from 'react';
import { 
    Modal, Box, Typography, TextField, Button, Grid, 
    FormControl, InputLabel, Select, MenuItem, CircularProgress 
} from '@mui/material';
import { useCurrency } from '../utils/currency';

export default function StayFeeFormModal({ open, onClose, onSave, editingFee = null, loading = false }) {
    const { currencySymbol } = useCurrency();
    const [formData, setFormData] = useState({
        type: 'LateCheckOut',
        feeType: 'Fixed',
        value: 0,
        thresholdTime: '14:00'
    });

    useEffect(() => {
        if (open) {
            if (editingFee) {
                setFormData({
                    type: editingFee.type || 'LateCheckOut',
                    feeType: editingFee.feeType || 'Fixed',
                    value: editingFee.value || 0,
                    thresholdTime: editingFee.thresholdTime || '14:00'
                });
            } else {
                setFormData({
                    type: 'LateCheckOut',
                    feeType: 'Fixed',
                    value: 0,
                    thresholdTime: '14:00'
                });
            }
        }
    }, [open, editingFee]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        const payload = {};
        const numericValue = parseFloat(formData.value);

        if (editingFee) {
            // Only send fields that have changed
            if (formData.type !== editingFee.type) payload.type = formData.type;
            if (formData.feeType !== editingFee.feeType) payload.feeType = formData.feeType;
            if (numericValue !== parseFloat(editingFee.value)) payload.value = numericValue;
            if (formData.thresholdTime !== editingFee.thresholdTime) payload.thresholdTime = formData.thresholdTime;
            
            // If nothing changed, just close
            if (Object.keys(payload).length === 0) {
                onClose();
                return;
            }
        } else {
            // New creation, send all fields
            payload.type = formData.type;
            payload.feeType = formData.feeType;
            payload.value = numericValue;
            payload.thresholdTime = formData.thresholdTime;
        }

        onSave(payload);
    };

    return (
        <Modal open={open} onClose={loading ? null : onClose}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: 450, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24
            }}>
                <Typography variant="h6" fontWeight="bold" mb={3}>
                    {editingFee ? 'Edit Stay Fee' : 'Create Stay Fee'}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Fee Type (Category)</InputLabel>
                                <Select
                                    name="type"
                                    value={formData.type}
                                    label="Fee Type (Category)"
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <MenuItem value="LateCheckOut">Late Check-Out</MenuItem>
                                    <MenuItem value="EarlyCheckIn">Early Check-In</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Calculation Method</InputLabel>
                                <Select
                                    name="feeType"
                                    value={formData.feeType}
                                    label="Calculation Method"
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <MenuItem value="Fixed">Fixed Amount</MenuItem>
                                    <MenuItem value="Percentage">Percentage of Daily Rate</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Fee Value"
                                name="value"
                                type="number"
                                value={formData.value}
                                onChange={handleChange}
                                required
                                size="small"
                                disabled={loading}
                                InputProps={{
                                    endAdornment: formData.feeType === 'Percentage' ? '%' : currencySymbol
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Threshold Time"
                                name="thresholdTime"
                                type="time"
                                value={formData.thresholdTime}
                                onChange={handleChange}
                                required
                                size="small"
                                disabled={loading}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button variant="outlined" onClick={onClose} disabled={loading}>
                                Cancel
                            </Button>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {loading ? (editingFee ? 'Saving...' : 'Creating...') : (editingFee ? 'Save Changes' : 'Create Fee')}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Modal>
    );
}
